// Lets people in the place talk to each other: each person shares their microphone, camera and
// screen, chooses which camera and microphone, sees everyone on the stage, one tile a person with
// their camera or their initials, hears what the others share, and sees the call of anyone there
// who gives no name.
//
// Talk: Collab opens this person's microphone, camera (the ones chosen in the settings menu) and
// screen with its sound itself (getUserMedia, getDisplayMedia) and sends their tracks with the SDK's
// Media, as `camera` (the camera and the microphone) and `screen` (the screen and its sound); each
// person's MediaStream is shown with the SDK's `Media.show`, the picture in their tile's <video> at
// once and the sound through the page from the first click, tap or key: a tile for each person
// there (their initials while they send no picture), one for each screen shared, and one for each
// `camera` of someone there who gave no name, shown by its path. Each tile also offers full screen
// and picture-in-picture (present.ts).

import { Media, type Connection } from 'tablebox.io';
import { avatar, h, icon, iconButton, languageSelect, setIcon, show, t, type Child, type IconName } from './dom.ts';
import { offered, present, type Presentation } from './present.ts';
import { hueOf, initials, type Key } from './text.ts';

/** Whether this browser can capture the screen (phones cannot). */
const canShareScreen = () => typeof navigator.mediaDevices?.getDisplayMedia === 'function';
const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** Each presentation's button: its text and its icon. */
const PRESENT: Record<Presentation, [Key, IconName]> = {
  'fullscreen': ['fullscreen', 'fullscreen'],
  'picture-in-picture': ['pictureInPicture', 'pip'],
};

/** What a tile shows: a person's camera (their initials while it sends no picture), a screen, or a stream from outside. */
type Kind = 'camera' | 'screen' | 'stream';

interface Tile {
  tile: HTMLElement;
  label: HTMLElement;
  /** The initials in the avatar layer. */
  face: HTMLElement;
  video: HTMLVideoElement;
  /** Whose call or screen it plays. */
  id: string;
  kind: Kind;
}

/** The path of `id`'s media of `name` (`camera` or `screen`), as the SDK publishes it. */
const pathOf = (id: string, name: 'camera' | 'screen') => `media/${name}/${id}`;

/**
 * Talk: the stage's tiles, the dock's switches, the settings menu and the notice of a failure.
 * `peopleIds` gives everyone there who gave a name, this person first.
 */
export function talkSection(connection: Connection, nameOf: (id: string) => string, isPerson: (id: string) => boolean, peopleIds: () => string[]): {
  tiles: HTMLElement; switches: HTMLElement[]; settingsMenu: HTMLElement; notice: HTMLElement;
  refresh: () => void; close: () => void;
} {
  const me = connection.id;
  const disposers: (() => void)[] = [];
  const notice = h('p', { class: 'banner', id: 'notice', role: 'alert' });
  const failed = (what: () => string, message: string) => show(notice, () => t('captureFailed', { what: what(), message }));
  // What this person sends, and everyone's, this person's own too.
  const call = Media.publish(connection, 'camera', new MediaStream());
  const screen = Media.publish(connection, 'screen', new MediaStream());
  const calls = Media.subscribe(connection, 'camera', () => update());
  const screens = Media.subscribe(connection, 'screen', () => update());
  /** The track this person sends for `kind`, as the SDK has it: a track the browser ended is gone. */
  const sending = (kind: 'camera' | 'microphone' | 'screen') => (kind === 'screen' ? screens.get(me)?.getVideoTracks()[0]
    : kind === 'camera' ? calls.get(me)?.getVideoTracks()[0] : calls.get(me)?.getAudioTracks()[0]);

  /** A choice among the browser's cameras or microphones, the browser's default while none is chosen. */
  const devices = (id: string, label: Key, kind: 'camera' | 'microphone') => {
    const select: HTMLSelectElement = h('select', { id, onchange: () => sending(kind) && void turn(kind, true) });
    const update = async () => {
      const all = await navigator.mediaDevices?.enumerateDevices().catch(() => []) ?? [];
      // Without the permission, devices have no ID: only the default is offered.
      const found = all.filter(info => info.kind === (kind === 'camera' ? 'videoinput' : 'audioinput') && info.deviceId && info.deviceId !== 'default');
      const value = select.value;
      const options: Child[] = [h('option', { value: '' }, () => t('defaultDevice'))];
      found.forEach((info, i) => options.push(h('option', { value: info.deviceId }, info.label || (() => t('device', { n: i + 1 })))));
      show(select, options);
      select.value = found.some(info => info.deviceId === value) ? value : '';
    };
    void update();
    return { select, update, element: h('label', {}, () => t(label), select) };
  };
  const chosen = { camera: devices('camera-device', 'camera', 'camera'), microphone: devices('microphone-device', 'microphone', 'microphone') };
  const listDevices = () => Object.values(chosen).forEach(each => void each.update());
  navigator.mediaDevices?.addEventListener('devicechange', listDevices);
  disposers.push(() => navigator.mediaDevices?.removeEventListener('devicechange', listDevices));

  /** Whether opening each is under way, so that a second press waits for it. */
  const opening = new Set<string>();
  /** Opens the camera or the microphone chosen and sends it in place of the one sent; or stops it. */
  const turn = async (kind: 'camera' | 'microphone', on: boolean) => {
    const before = sending(kind);
    // The other of the camera and the microphone stays as it is sent.
    const send = (track: MediaStreamTrack | null) => call.set(new MediaStream([sending(kind === 'camera' ? 'microphone' : 'camera'), track].filter(each => each !== null && each !== undefined)));
    if (!on) {
      send(null);
      before?.stop();
      return;
    }
    if (opening.has(kind)) return;
    opening.add(kind);
    const device = chosen[kind].select.value;
    try {
      const constraints = kind === 'camera'
        ? { video: { width: { ideal: 1280 }, height: { ideal: 720 }, ...(device && { deviceId: { exact: device } }) } }
        : { audio: device ? { deviceId: { exact: device } } : true };
      send((await navigator.mediaDevices.getUserMedia(constraints)).getTracks()[0]);
      before?.stop();
      show(notice);
      listDevices();
    } catch (error) {
      failed(() => t(kind), messageOf(error));
    } finally {
      opening.delete(kind);
    }
  };
  /** Shares a screen the person picks, with its sound where the browser gives it, or stops sharing. */
  const share = async (on: boolean) => {
    const before = screens.get(me)?.getTracks() ?? [];
    if (!on) {
      screen.set(new MediaStream());
      before.forEach(track => track.stop());
      return;
    }
    if (opening.has('screen')) return;
    opening.add('screen');
    try {
      const captured = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      screen.set(captured);
      before.forEach(track => track.stop());
      show(notice);
    } catch (error) {
      // A picker the person cancelled shares nothing and says nothing.
      if ((error as Error)?.name !== 'NotAllowedError') failed(() => t('screen'), messageOf(error));
    } finally {
      opening.delete('screen');
    }
  };

  /** A round switch for what `kind` sends, set with `set`, showing its state by its icon. */
  const switches: HTMLElement[] = [];
  const updates: (() => void)[] = [];
  const toggle = (id: string, kind: 'camera' | 'microphone' | 'screen', set: (on: boolean) => void, label: Key, on: IconName, off: IconName) => {
    const button = iconButton({ id, 'aria-pressed': 'false', onclick: () => set(!sending(kind)) }, on, label);
    const update = () => {
      button.setAttribute('aria-pressed', String(sending(kind) !== undefined));
      setIcon(button.querySelector('svg')!, sending(kind) ? on : off);
    };
    update();
    updates.push(update);
    switches.push(button);
  };
  toggle('microphone', 'microphone', on => void turn('microphone', on), 'microphone', 'mic', 'mic-off');
  toggle('camera', 'camera', on => void turn('camera', on), 'camera', 'camera', 'camera-off');
  // The browser's own Stop ends the capture, which the SDK counts as stopped, and the switch.
  if (canShareScreen()) toggle('screen', 'screen', on => void share(on), 'screen', 'screen', 'screen');

  // The person's settings: the camera and microphone to use, and the language Collab shows.
  const settingsMenu = h('div', { id: 'settings-menu', popover: 'auto' },
    h('p', { class: 'menu-title' }, () => t('settings')),
    chosen.camera.element,
    chosen.microphone.element,
    h('label', { class: 'menu-language' }, () => t('language'), languageSelect()));

  // The stage: a tile for each person, each screen shared, and each stream from outside, by path.
  const grid = h('div', { id: 'tiles', 'data-count': '0' });
  const tiles = new Map<string, Tile>();
  const count = () => (grid.dataset.count = String(grid.childElementCount));
  /** The tile shown large, as LiveKit Meet's focus: tapping a tile enlarges it, tapping it again returns it. */
  let focused: string | undefined;
  const focus = (path: string | undefined) => {
    focused = path;
    for (const [each, { tile }] of tiles) tile.setAttribute('aria-pressed', String(each === focused));
    grid.toggleAttribute('data-focus', focused !== undefined);
  };
  const labelOf = (path: string, { id, kind }: { id: string; kind: Kind }): string => {
    if (kind === 'stream') return path;
    const name = nameOf(id);
    if (kind === 'screen') return t('screenOf', { name });
    return id === me ? `${name} ${t('you')}` : name;
  };
  const mediaOf = (path: string, id: string) => (path === pathOf(id, 'screen') ? screens : calls).get(id);

  const make = (path: string, id: string, kind: Kind): Tile => {
    const label = h('span', { class: 'tile-name' });
    const actions = h('span', { class: 'tile-actions' });
    const face = avatar(id, '');
    const video = h('video', { playsinline: true, autoplay: true });
    const toggleFocus = () => focus(focused === path ? undefined : path);
    const tile = h('figure', { class: 'tile', 'data-path': path, 'data-live': 'false', role: 'button', tabindex: '0', 'aria-pressed': 'false', onclick: toggleFocus,
      onkeydown: (event: KeyboardEvent) => {
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        toggleFocus();
      } }, video, face, h('figcaption', {}, label, actions));
    // The presentations the browser offers, each started inside its button's click, of the tile's own <video>.
    show(actions, [...offered()].map(how => h('button', {
      type: 'button', class: 'tile-action', 'data-present': how, 'aria-label': () => t(PRESENT[how][0]), title: () => t(PRESENT[how][0]),
      onclick: (event: MouseEvent) => {
        event.stopPropagation();
        present(video, how).catch((error: unknown) => failed(() => t(PRESENT[how][0]), messageOf(error)));
      },
    }, icon(PRESENT[how][1]))));
    return { tile, label, face: face.firstElementChild as HTMLElement, video, id, kind };
  };

  function update() {
    for (const each of updates) each();
    // Everyone there has a camera tile; a screen has one while it is shared, and a stream from outside while it is there.
    const wanted = new Map<string, { id: string; kind: Kind }>(peopleIds().map(id => [pathOf(id, 'camera'), { id, kind: 'camera' }]));
    for (const id of peopleIds()) if (screens.get(id)) wanted.set(pathOf(id, 'screen'), { id, kind: 'screen' });
    for (const id of calls.keys()) if (!isPerson(id)) wanted.set(pathOf(id, 'camera'), { id, kind: 'stream' });
    for (const [path, want] of wanted) {
      let entry = tiles.get(path);
      if (!entry) {
        entry = make(path, want.id, want.kind);
        tiles.set(path, entry);
        grid.append(entry.tile);
      }
      const media = mediaOf(path, want.id);
      // This person's own tile plays without sound, so that their voice does not echo.
      Media.show(entry.video, media);
      entry.kind = want.kind;
      entry.tile.dataset.kind = want.kind;
      entry.tile.dataset.live = String(want.kind !== 'camera' || (media?.getVideoTracks().length ?? 0) > 0);
      entry.tile.classList.toggle('stream-tile', want.kind === 'stream');
      entry.tile.setAttribute('aria-label', labelOf(path, want));
      show(entry.label, () => labelOf(path, want));
      const name = nameOf(want.id);
      entry.face.textContent = initials(name);
      entry.face.parentElement?.style.setProperty('--hue', String(hueOf(name)));
    }
    for (const [path, entry] of tiles) {
      if (wanted.has(path)) continue;
      Media.show(entry.video, undefined);
      entry.tile.remove();
      tiles.delete(path);
      if (focused === path) focus(undefined);
    }
    count();
  }
  update();

  return {
    tiles: grid,
    switches,
    settingsMenu,
    notice,
    refresh: update,
    close() {
      for (const dispose of disposers) dispose();
      for (const entry of tiles.values()) Media.show(entry.video, undefined);
      for (const track of [...calls.get(me)?.getTracks() ?? [], ...screens.get(me)?.getTracks() ?? []]) track.stop();
      calls.close();
      screens.close();
      call.close();
      screen.close();
    },
  };
}
